/* eslint-disable */
import {
  AjvReflection,
  DomainEntityId,
  DomainEntityReflection,
  EntityReflections,
  EnumLike,
  EnumReflection,
  RawAliasReflection,
  SchemaOfObject,
} from '@salsita-ai/platform-project-sdk/domain'

export const modelVersion = 1

export interface GenericDomainEntity {
  readonly id: DomainEntityId<string>
  readonly type: string
  [k: string]: unknown
}
export const GenericDomainEntity: AjvReflection<GenericDomainEntity> = new AjvReflection('GenericDomainEntity', {
  type: 'object',
  properties: {
    id: {
      type: 'string',
      pattern: '^[A-Z]\\w*-\\d+$',
    },
    type: {
      type: 'string',
    },
  },
  additionalProperties: true,
  required: ['id', 'type'],
})

export interface RecipeConfiguration {
  readonly modelVersion: number
  readonly entities: readonly GenericDomainEntity[]
  readonly rootEntityType: string
  readonly rootRecipeId: string
  readonly recipeIds: readonly string[]
  readonly configurationId: string
  readonly revisionId: string
  readonly ownerToken: string
}
export const RecipeConfiguration: AjvReflection<RecipeConfiguration> = new AjvReflection('RecipeConfiguration', {
  type: 'object',
  properties: {
    modelVersion: {
      type: 'integer',
    },
    entities: {
      type: 'array',
      items: GenericDomainEntity._meta.schema,
    },
    rootEntityType: {
      type: 'string',
    },
    rootRecipeId: {
      type: 'string',
    },
    recipeIds: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
    configurationId: {
      type: 'string',
    },
    revisionId: {
      type: 'string',
    },
    ownerToken: {
      type: 'string',
    },
  },
  additionalProperties: false,
  required: [
    'entities',
    'rootEntityType',
    'rootRecipeId',
    'recipeIds',
    'modelVersion',
    'configurationId',
    'revisionId',
    'ownerToken',
  ],
})

export type Finish = 'brushed' | 'milled' | 'chequer' | 'propeller'
export const Finish: EnumLike<Finish> = new EnumReflection('Finish', {
  enum: ['brushed', 'milled', 'chequer', 'propeller'],
}) as EnumLike<Finish>

export type RawMaterial = 'brass' | 'ss304' | 'ss316' | 'aluminium'
export const RawMaterial: EnumLike<RawMaterial> = new EnumReflection('RawMaterial', {
  enum: ['brass', 'ss304', 'ss316', 'aluminium'],
}) as EnumLike<RawMaterial>

export type AnchorPointPosition =
  | 'topLeft'
  | 'topRight'
  | 'bottomLeft'
  | 'bottomRight'
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'center'
export const AnchorPointPosition: EnumLike<AnchorPointPosition> = new EnumReflection('AnchorPointPosition', {
  enum: ['topLeft', 'topRight', 'bottomLeft', 'bottomRight', 'top', 'bottom', 'left', 'right', 'center'],
}) as EnumLike<AnchorPointPosition>

interface CommonAdminEntityShape {
  readonly commonAdminModelVersion: 3
}
const CommonAdminEntityShape = new AjvReflection<CommonAdminEntity, SchemaOfObject<CommonAdminEntityShape>>(
  'CommonAdminEntityShape',
  {
    type: 'object',
    properties: {
      commonAdminModelVersion: {
        enum: [3],
      },
    },
    additionalProperties: false,
    required: ['commonAdminModelVersion'],
  }
)

interface PresetCategoryShape {
  readonly name: string
  readonly index: number
  readonly visible: boolean
  readonly description: string
}
const PresetCategoryShape = new AjvReflection<PresetCategory, SchemaOfObject<PresetCategoryShape>>(
  'PresetCategoryShape',
  {
    type: 'object',
    properties: {
      name: {
        type: 'string',
      },
      index: {
        type: 'number',
      },
      visible: {
        type: 'boolean',
      },
      description: {
        type: 'string',
      },
    },
    additionalProperties: false,
    required: ['name', 'index', 'visible', 'description'],
  }
)

interface PresetShape {
  readonly name: string
  readonly description: string
  readonly index: number
  readonly visible: boolean
  readonly categoryId: PresetCategory['id'] | null
  readonly configurationId: string | null
  readonly revisionId: string | null
  readonly ownerToken: string | null
  readonly readToken: string | null
  readonly thumbnailUrl: string | null
  readonly tags: readonly string[]
}
const PresetShape = new AjvReflection<Preset, SchemaOfObject<PresetShape>>('PresetShape', {
  type: 'object',
  properties: {
    name: {
      type: 'string',
    },
    description: {
      type: 'string',
    },
    index: {
      type: 'number',
    },
    visible: {
      type: 'boolean',
    },
    categoryId: {
      oneOf: [
        {
          type: 'string',
          pattern: '^[A-Z]\\w*-\\d+$',
        },
        {
          type: 'null',
        },
      ],
    },
    configurationId: {
      type: ['string', 'null'],
    },
    revisionId: {
      type: ['string', 'null'],
    },
    ownerToken: {
      type: ['string', 'null'],
    },
    readToken: {
      type: ['string', 'null'],
    },
    thumbnailUrl: {
      type: ['string', 'null'],
    },
    tags: {
      type: 'array',
      items: {
        type: 'string',
      },
    },
  },
  additionalProperties: false,
  required: [
    'name',
    'description',
    'index',
    'visible',
    'categoryId',
    'configurationId',
    'revisionId',
    'ownerToken',
    'readToken',
    'thumbnailUrl',
    'tags',
  ],
})

interface RecipeShape {
  readonly name: string
  readonly description: string
  readonly collection: string
  readonly index: number
  readonly visible: boolean
  readonly configuration: RecipeConfiguration | null
}
const RecipeShape = new AjvReflection<Recipe, SchemaOfObject<RecipeShape>>('RecipeShape', {
  type: 'object',
  properties: {
    name: {
      type: 'string',
    },
    description: {
      type: 'string',
    },
    collection: {
      type: 'string',
    },
    index: {
      type: 'number',
    },
    visible: {
      type: 'boolean',
    },
    configuration: {
      anyOf: [
        RecipeConfiguration._meta.schema,
        {
          type: 'null',
        },
      ],
    },
  },
  additionalProperties: false,
  required: ['name', 'description', 'collection', 'index', 'visible', 'configuration'],
})

interface CustomMaterialShape {
  readonly code: string
  readonly name: string
  readonly length: number
  readonly width: number
  readonly thickness: number
  readonly rawMaterial: RawMaterial
  readonly finish: Finish
  readonly visible: boolean
  readonly standard: boolean
}
const CustomMaterialShape = new AjvReflection<CustomMaterial, SchemaOfObject<CustomMaterialShape>>(
  'CustomMaterialShape',
  {
    type: 'object',
    properties: {
      code: {
        type: 'string',
      },
      name: {
        type: 'string',
      },
      length: {
        type: 'number',
      },
      width: {
        type: 'number',
      },
      thickness: {
        type: 'number',
      },
      rawMaterial: RawMaterial._meta.schema,
      finish: Finish._meta.schema,
      visible: {
        type: 'boolean',
      },
      standard: {
        type: 'boolean',
      },
    },
    additionalProperties: false,
    required: ['code', 'name', 'length', 'width', 'thickness', 'rawMaterial', 'finish', 'visible', 'standard'],
  }
)

interface CountersinkShape {
  readonly topRadius: number
  readonly bottomRadius: number
  readonly plateThicknessMin: number
  readonly plateThicknessMax: number
  readonly visible: boolean
}
const CountersinkShape = new AjvReflection<Countersink, SchemaOfObject<CountersinkShape>>('CountersinkShape', {
  type: 'object',
  properties: {
    topRadius: {
      type: 'number',
    },
    bottomRadius: {
      type: 'number',
    },
    plateThicknessMin: {
      type: 'number',
    },
    plateThicknessMax: {
      type: 'number',
    },
    visible: {
      type: 'boolean',
    },
  },
  additionalProperties: false,
  required: ['topRadius', 'bottomRadius', 'plateThicknessMin', 'plateThicknessMax', 'visible'],
})

interface FeatureSettingShape {
  readonly plateType: string
  readonly featureType: string
  readonly offsetX: number
  readonly offsetY: number
  readonly anchorPointPosition: AnchorPointPosition
}
const FeatureSettingShape = new AjvReflection<FeatureSetting, SchemaOfObject<FeatureSettingShape>>(
  'FeatureSettingShape',
  {
    type: 'object',
    properties: {
      plateType: {
        type: 'string',
      },
      featureType: {
        type: 'string',
      },
      offsetX: {
        type: 'number',
      },
      offsetY: {
        type: 'number',
      },
      anchorPointPosition: AnchorPointPosition._meta.schema,
    },
    additionalProperties: false,
    required: ['plateType', 'featureType', 'offsetX', 'offsetY', 'anchorPointPosition'],
  }
)

export interface Admin extends CommonAdminEntityShape {
  readonly id: DomainEntityId<'Admin'>
  readonly type: 'Admin'
}
export const Admin: DomainEntityReflection<Admin> = new DomainEntityReflection<Admin>('Admin', [
  CommonAdminEntityShape._meta.schema,
])

export interface Preset extends PresetShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'Preset'>
  readonly type: 'Preset'
}
export const Preset: DomainEntityReflection<Preset> = new DomainEntityReflection<Preset>('Preset', [
  PresetShape._meta.schema,
  CommonAdminEntityShape._meta.schema,
])

export interface PresetCategory extends PresetCategoryShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'PresetCategory'>
  readonly type: 'PresetCategory'
}
export const PresetCategory: DomainEntityReflection<PresetCategory> = new DomainEntityReflection<PresetCategory>(
  'PresetCategory',
  [PresetCategoryShape._meta.schema, CommonAdminEntityShape._meta.schema]
)

export interface Recipe extends RecipeShape, CommonAdminEntityShape {
  readonly id: DomainEntityId<'Recipe'>
  readonly type: 'Recipe'
}
export const Recipe: DomainEntityReflection<Recipe> = new DomainEntityReflection<Recipe>('Recipe', [
  RecipeShape._meta.schema,
  CommonAdminEntityShape._meta.schema,
])

export interface DummyDoNotUse {
  readonly id: DomainEntityId<'DummyDoNotUse'>
  readonly type: 'DummyDoNotUse'
}
export const DummyDoNotUse: DomainEntityReflection<DummyDoNotUse> = new DomainEntityReflection<DummyDoNotUse>(
  'DummyDoNotUse',
  []
)

export interface CustomMaterial extends CustomMaterialShape {
  readonly id: DomainEntityId<'CustomMaterial'>
  readonly type: 'CustomMaterial'
}
export const CustomMaterial: DomainEntityReflection<CustomMaterial> = new DomainEntityReflection<CustomMaterial>(
  'CustomMaterial',
  [CustomMaterialShape._meta.schema]
)

export interface Countersink extends CountersinkShape {
  readonly id: DomainEntityId<'Countersink'>
  readonly type: 'Countersink'
}
export const Countersink: DomainEntityReflection<Countersink> = new DomainEntityReflection<Countersink>('Countersink', [
  CountersinkShape._meta.schema,
])

export interface FeatureSetting extends FeatureSettingShape {
  readonly id: DomainEntityId<'FeatureSetting'>
  readonly type: 'FeatureSetting'
}
export const FeatureSetting: DomainEntityReflection<FeatureSetting> = new DomainEntityReflection<FeatureSetting>(
  'FeatureSetting',
  [FeatureSettingShape._meta.schema]
)

export type AnyDomainEntity =
  | Admin
  | Preset
  | PresetCategory
  | Recipe
  | DummyDoNotUse
  | CustomMaterial
  | Countersink
  | FeatureSetting
export const AnyDomainEntity: RawAliasReflection<AnyDomainEntity> = new RawAliasReflection<AnyDomainEntity>(
  'AnyDomainEntity',
  [Admin, Preset, PresetCategory, Recipe, DummyDoNotUse, CustomMaterial, Countersink, FeatureSetting]
)

export type RootEntity = Admin
export const RootEntity: RawAliasReflection<RootEntity> = new RawAliasReflection<RootEntity>('RootEntity', [Admin])

export type CommonAdminEntity = Admin | Preset | PresetCategory | Recipe
export const CommonAdminEntity: RawAliasReflection<CommonAdminEntity> = new RawAliasReflection<CommonAdminEntity>(
  'CommonAdminEntity',
  [Admin, Preset, PresetCategory, Recipe]
)

export const aliases = {
  AnyDomainEntity: AnyDomainEntity.types,
  RootEntity: RootEntity.types,
  CommonAdminEntity: CommonAdminEntity.types,
} as const
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type Aliases = { AnyDomainEntity: AnyDomainEntity; RootEntity: RootEntity; CommonAdminEntity: CommonAdminEntity }

export const entities: EntityReflections<AnyDomainEntity> = {
  Admin,
  Preset,
  PresetCategory,
  Recipe,
  DummyDoNotUse,
  CustomMaterial,
  Countersink,
  FeatureSetting,
} as const
